import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'switch',
  version: '1.0.0',
  properties: [
    // ===== MODE SELECTOR =====
    {
      key: 'mode',
      label: 'Mode',
      type: 'options',
      required: true,
      default: 'rules',
      description: 'How to determine which output to route data to',
      options: [
        { value: 'rules', name: 'Rules', description: 'Create conditions for each output' },
        { value: 'expression', name: 'Expression', description: 'Use expression to return output index' },
      ],
    },

    // ===== RULES MODE =====
    {
      key: 'rules',
      label: 'Routing Rules',
      type: 'fixedCollection',
      required: true,
      typeOptions: {
        multipleValues: true,
      },
      default: {
        values: [
          {
            outputName: '',
            conditions: {
              conditions: [
                { leftValue: '', operator: 'equals', rightValue: '' }
              ]
            },
            conditionCombinator: 'and',
          },
        ],
      },
      fixedCollectionOptions: [
        {
          name: 'values',
          displayName: 'Rule',
          description: 'Each rule creates an output. Data matching the rule is routed to that output.',
          values: [
            {
              key: 'outputName',
              label: 'Output Name',
              type: 'string',
              default: '',
              placeholder: 'e.g., "Approved", "Rejected"',
              description: 'Optional label for this output (shown in editor)',
            },
            {
              key: 'conditions',
              label: 'Conditions',
              type: 'fixedCollection',
              typeOptions: {
                multipleValues: true,
              },
              default: {
                conditions: [
                  { leftValue: '', operator: 'equals', rightValue: '' }
                ],
              },
              fixedCollectionOptions: [
                {
                  name: 'conditions',
                  displayName: 'Condition',
                  values: [
                    {
                      key: 'leftValue',
                      label: 'Value 1',
                      type: 'string',
                      required: true,
                      placeholder: '{{ $input.status }}',
                      description: 'First value to compare (use {{ }} for expressions)',
                    },
                    {
                      key: 'operator',
                      label: 'Operator',
                      type: 'options',
                      required: true,
                      default: 'equals',
                      options: [
                        // String operators
                        { value: 'equals', name: 'Equals' },
                        { value: 'notEquals', name: 'Not Equals' },
                        { value: 'contains', name: 'Contains' },
                        { value: 'notContains', name: 'Not Contains' },
                        { value: 'startsWith', name: 'Starts With' },
                        { value: 'endsWith', name: 'Ends With' },
                        { value: 'regex', name: 'Matches Regex' },
                        // Numeric operators
                        { value: 'greaterThan', name: 'Greater Than' },
                        { value: 'lessThan', name: 'Less Than' },
                        { value: 'greaterThanOrEqual', name: 'Greater Than or Equal' },
                        { value: 'lessThanOrEqual', name: 'Less Than or Equal' },
                        // Existence operators
                        { value: 'isEmpty', name: 'Is Empty' },
                        { value: 'isNotEmpty', name: 'Is Not Empty' },
                        { value: 'exists', name: 'Exists' },
                        { value: 'notExists', name: 'Does Not Exist' },
                        // Type operators
                        { value: 'isTrue', name: 'Is True' },
                        { value: 'isFalse', name: 'Is False' },
                        { value: 'isNumber', name: 'Is Number' },
                        { value: 'isArray', name: 'Is Array' },
                        { value: 'isObject', name: 'Is Object' },
                      ],
                    },
                    {
                      key: 'rightValue',
                      label: 'Value 2',
                      type: 'string',
                      placeholder: '"active"',
                      description: 'Value to compare against',
                      displayOptions: {
                        hide: {
                          operator: [
                            'isEmpty', 'isNotEmpty', 'exists', 'notExists',
                            'isTrue', 'isFalse', 'isNumber', 'isArray', 'isObject'
                          ],
                        },
                      },
                    },
                  ],
                },
              ],
            },
            {
              key: 'conditionCombinator',
              label: 'Combine Conditions',
              type: 'options',
              default: 'and',
              options: [
                { value: 'and', name: 'AND (all must match)' },
                { value: 'or', name: 'OR (any must match)' },
              ],
            },
          ],
        },
      ],
      displayOptions: {
        show: {
          mode: ['rules'],
        },
      },
    },

    // ===== EXPRESSION MODE =====
    {
      key: 'numberOfOutputs',
      label: 'Number of Outputs',
      type: 'number',
      default: 4,
      required: true,
      typeOptions: {
        minValue: 2,
        maxValue: 10,
      },
      description: 'Number of outputs available for routing',
      displayOptions: {
        show: {
          mode: ['expression'],
        },
      },
    },
    {
      key: 'outputExpression',
      label: 'Output Expression',
      type: 'string',
      required: true,
      placeholder: '{{ $input.category === "A" ? 0 : 1 }}',
      description: 'Expression that returns the output index (0-based)',
      displayOptions: {
        show: {
          mode: ['expression'],
        },
      },
    },

    // ===== OPTIONS (both modes) =====
    {
      key: 'fallbackOutput',
      label: 'Fallback Output',
      type: 'options',
      default: 'none',
      options: [
        { value: 'none', name: 'None (stop execution)' },
        { value: 'extra', name: 'Extra Output (add "Fallback" output)' },
        { value: '0', name: 'Output 0 (first output)' },
      ],
      description: 'What to do when no rules match',
    },
    {
      key: 'ignoreCase',
      label: 'Ignore Case',
      type: 'boolean',
      default: false,
      description: 'Ignore letter case when comparing strings',
    },
    {
      key: 'sendToAll',
      label: 'Send to All Matching Outputs',
      type: 'boolean',
      default: false,
      description: 'Send data to all outputs with matching rules (not just the first)',
    },
  ],
};

export default schema;
